import type { DropdownFilterCallback } from '../../components/Dropdown/Dropdown';
import type { FormApi } from '../../form-hooks/useFormApi';
import type { WebhookFormSubmitFn } from '../../form/WebhookForm/WebhookForm';
import type {
  ContentObject,
  ContentTypeDefinition,
  Webhook,
} from '../../lib/flotiq-client/types/api-response';

import { use, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { listContentTypes, postWebhooks } from '../../lib/flotiq-client';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';
import { FormAddSidebarPanelEvent } from '../../lib/flotiq-plugins/plugin-events/FormAddSidebarPanelEvent';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import ContentObjectInformation from '../../components/ContentObjectInformation/ContentObjectInformation';
import ContentObjectVersions from '../../components/ContentObjectVersions/ContentObjectVersions';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarDuplicateButton from '../../components/Topbar/buttons/TopbarDuplicateButton';
import TopbarSaveAndLeaveButton from '../../components/Topbar/buttons/TopbarSaveAndLeaveButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';
import WebhookLogs from '../../components/WebhookLogs/WebhookLogs';

import {
  useContentObject,
  useContentObjectVersions,
  useContentType,
  useContentTypes,
  useWebhookLogs,
} from '../../hooks/api';
import { useAllSpaceConstraints } from '../../hooks/api/useAllSpaceConstraints';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import { useDuplicateModal } from '../../hooks/useDuplicateModal/useDuplicateModal';
import usePluginResults from '../../hooks/usePluginResults';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { ModalInstanceContext, useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import WebhookForm from '../../form/WebhookForm/WebhookForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';
import PoppingPageLayout from '../../layout/PageLayout/PoppingPageLayout';

const DEFAULT_CODE_EDITOR =
  'function _webhook(ctx) {\n' +
  '        const { payload } = ctx;\n' +
  '        const newPayload = { ...payload };\n\n' +
  '        // Example: Payload validation\n' +
  "        // if ( payload['category'] === 'book' && !payload['isbn'])\n" +
  "        // return { errors: {'isbn': ['Error: ISBN is required for books.'] } };\n\n" +
  '        // Example: Payload modifications\n' +
  "        // newPayload['slug'] = payload['title']\n" +
  '        //     .toLowerCase()\n' +
  "        //     .replace(/[^a-z0-9]+/g, '-')\n" +
  "        //     .replace(/(^-|-$)/g, '');\n\n" +
  '        return { payload: newPayload };\n' +
  '}';

const VERSION_HOOK_PARAMS = {
  limit: 1000,
  page: 1,
};

interface AddWebhooksProps {
  /**
   * current edite Webhook id
   */
  webhookId?: string;
  /**
   * If is on duplicate
   */
  duplicate?: boolean;
  /**
   * Current mode "edit" or "add"
   */
  mode?: string;
  /**
   * Test id for page
   */
  testId?: string;
}

const AddWebhooksContainer = ({
  webhookId = undefined,
  mode = 'edit',
  duplicate = false,
  testId = '',
}: AddWebhooksProps) => {
  const { t } = useTranslation();
  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const { permissions } = use(UserContext);
  const jwt = useToken();
  const modal = useModals();
  const navigateOnSave = useRef<boolean>(false);
  const navigate = useNavigate();

  const [formState, setFormState] = useState<FormApi>();
  const [isSaving, setIsSaving] = useState(false);

  const modalInstance = use(ModalInstanceContext);
  const inModal = !!modalInstance?.resolve;

  const { navigateGrid, gridLink } = useGridNavigate(
    `objects-_webhooks`,
    buildUrlWithSpace(`webhooks`),
  );

  const {
    entity: webhook,
    isLoading: webhookLoading,
    errors,
    deleteEntity: deleteWebhook,
    updateEntity: updateWebhook,
    partialUpdateEntity: partialUpdateWebhook,
  } = useContentObject('_webhooks', webhookId);

  useApiErrorsToast(errors);

  const navigateHandler = useCallback(() => {
    if (inModal) {
      modalInstance.resolve?.(webhook);
      return;
    }

    navigateGrid();
  }, [inModal, modalInstance, navigateGrid, webhook]);

  useEffect(() => {
    if (webhookId && !webhook && !webhookLoading) {
      navigateHandler();
    }
  }, [webhookLoading, navigateHandler, webhook, webhookId]);

  const versionsHookOptions = useMemo(
    () => ({ pause: !webhookId || duplicate }),
    [duplicate, webhookId],
  );

  const webhookLogsParams = useMemo(
    () => ({
      id: webhookId,
    }),
    [webhookId],
  );

  const {
    data: webhookLogs,
    isLoading: webhookLogsLoading,
    reload: webhookLogsReload,
    errors: webhookLogsErrors,
  } = useWebhookLogs(webhookLogsParams, versionsHookOptions);

  useApiErrorsToast(webhookLogsErrors);

  const { entity: webhookContentType, isLoading: webhookContentTypeLoading } =
    useContentType('_webhooks');

  const { onDuplicate } = useDuplicateModal(webhookContentType);

  const { canCreate, canDelete, canUpdate } = useMemo(
    () => permissions.getCoPermissions('_webhooks') || {},
    [permissions],
  );

  const codeWebhookLimit = spaceLimits?.hosted_webhooks_limit;

  const { allConstraints, reload: reloadAllConstraints } =
    useAllSpaceConstraints();

  const hostedWebhooksCount = allConstraints?.['hosted-webhooks-count'] || 0;

  const reloadWebhookConstraints = useCallback(() => {
    reloadAllConstraints([
      'hosted-webhooks-count',
      'content-objects-count',
      'webhooks-count',
    ]);
  }, [reloadAllConstraints]);

  const codeWebhookAvailable = useMemo(
    () =>
      codeWebhookLimit !== -1 ? hostedWebhooksCount < codeWebhookLimit : true,
    [codeWebhookLimit, hostedWebhooksCount],
  );

  const {
    data: webhookVersions,
    errors: webhookVersionsErrors,
    reload: reloadWebhookVersions,
  } = useContentObjectVersions(
    '_webhooks',
    webhookId,
    VERSION_HOOK_PARAMS,
    versionsHookOptions,
  );

  useApiErrorsToast(webhookVersionsErrors);

  const disableSave =
    !codeWebhookAvailable && formState?.getValue('executionMode') === 'code';

  const handleDeleteWebhook = useCallback(async () => {
    const result = await modal.delete(t('Webhooks.DeleteModalText'));

    if (result) {
      try {
        const { body, status, ok } = await deleteWebhook({
          contentTypeName: '_webhooks',
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        navigateHandler();
        reloadWebhookConstraints();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Webhooks.Error'));
        } else {
          toast.error(error.message);
        }
      }
    }
  }, [modal, t, deleteWebhook, navigateHandler, reloadWebhookConstraints]);

  const validateCodeEditorValue = useCallback(
    (code: string, type: string, mode: string) => {
      // Case: code value should be empty if
      // - default editor code was not changed by the user and
      //   execution mode is switch to "url"
      // - when selected type is not 'sync'
      if (type !== 'sync' || (mode === 'url' && code === DEFAULT_CODE_EDITOR)) {
        return '';
      }
      return code;
    },
    [],
  );

  const handleSave = useCallback<WebhookFormSubmitFn>(
    async (values) => {
      setIsSaving(true);

      const updateShape = {
        name: values?.name,
        url: values?.url,
        enabled: values?.enabled,
        type: values?.type,
        executionMode: values?.executionMode || 'url',
        code: validateCodeEditorValue(
          values?.code,
          values?.type,
          values?.executionMode,
        ),
        headers: values?.headers,
        ...(values?.type !== 'ctd'
          ? {
              actions: values?.actions,
              content_type_definitions: values?.contentTypeDefinitions?.map(
                (content_type_definition_name) => {
                  return { content_type_definition_name };
                },
              ),
            }
          : { actions: [], content_type_definitions: [] }),
      };

      for (const property in updateShape) {
        if (
          !permissions.canCo(
            '_webhooks',
            mode === 'edit'
              ? RolePermissions.PERMISSIONS_TYPES.UPDATE
              : RolePermissions.PERMISSIONS_TYPES.CREATE,
            property,
          )
        ) {
          delete updateShape[property as keyof typeof updateShape];
        }
      }

      if (mode === 'edit' && !duplicate) {
        const updateFunc = permissions.hasCoLimitedFields('_webhooks')
          ? partialUpdateWebhook
          : updateWebhook;

        try {
          const { body, status, ok } = await updateFunc({
            ...updateShape,
            id: webhookId,
            contentTypeName: '_webhooks',
          });
          setIsSaving(false);

          if (!ok) {
            throw parseResponseErrors(body, status);
          }

          await reloadWebhookVersions();
          toast.success(`${t('Webhooks.SavedOnSuccess')}`);

          if (navigateOnSave.current) {
            navigateHandler();
          }
          return [body as Webhook, null];
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('Webhooks.SavedOnError'));
            return [null, {}];
          } else {
            toast.error(error.message);
          }
          return [null, error.errors];
        }
      } else {
        try {
          const { body, status, ok } = await postWebhooks(
            jwt,
            space,
            updateShape,
          );
          setIsSaving(false);
          if (!ok) {
            throw parseResponseErrors(body, status);
          }

          toast.success(`${t('Webhooks.AddedOnSuccess')}`);
          if (navigateOnSave.current) {
            navigateHandler();
          } else {
            navigate(buildUrlWithSpace(`webhooks/edit/${body.id}`));
          }
          reloadWebhookConstraints();
          return [body as Webhook, null];
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('Webhooks.SavedOnError'));
            return [null, {}];
          } else {
            toast.error(error.message);
            return [null, error.errors];
          }
        }
      }
    },
    [
      validateCodeEditorValue,
      mode,
      duplicate,
      permissions,
      partialUpdateWebhook,
      updateWebhook,
      webhookId,
      reloadWebhookVersions,
      t,
      navigateHandler,
      jwt,
      space,
      reloadWebhookConstraints,
      navigate,
      buildUrlWithSpace,
    ],
  );

  const ctdParams = useMemo(
    () => ({
      limit: 1000,
      order_by: 'label',
      order_direction: 'asc',
      page: 1,
    }),
    [],
  );

  const {
    data: contentTypes,
    isLoading: isLoadingCT,
    errors: ctdErrors,
  } = useContentTypes(ctdParams);

  useApiErrorsToast(ctdErrors);

  const contentTypesOptions = useMemo(() => {
    if (!contentTypes) return;

    return contentTypes
      .filter(
        (el) => !(el.internal && el.name !== '_tag' && el.name !== '_media'),
      )
      .map((el) => {
        return {
          value: el.name,
          label: el.label,
        };
      });
  }, [contentTypes]);

  const filterCtdName = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      setIsLoading(true);
      let newOptions: ContentTypeDefinition[] = [];
      try {
        const { body, status, ok } = await listContentTypes(jwt, space, {
          ...ctdParams,
          name: query,
        });
        if (!ok) {
          throw parseResponseErrors(body, status);
        }
        newOptions = body.data || [];
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'ContentForm.CouldntFetch'
              : 'Form.CommunicationErrorMessage',
          ),
        );
      }

      setIsLoading(false);
      return newOptions
        .filter((ctd) => !ctd.internal || ctd.name === '_media')
        .map((ctd) => ({ label: ctd.label, value: ctd.name }));
    },
    [jwt, ctdParams, space, t],
  );

  const handleVersionRestore = useCallback(
    async (webhook: ContentObject) => {
      if (formState) {
        formState.setValues(webhook);
        await formState.submitForm();
      }
    },
    [formState],
  );

  const isLoading = isLoadingCT || webhookLoading || webhookContentTypeLoading;

  const isFormDisabled = useMemo(() => {
    if (isLoading || isSaving) return true;
    if (webhookId) return !canUpdate;
    return !canCreate;
  }, [canCreate, canUpdate, webhookId, isLoading, isSaving]);

  /**
   * @emits FlotiqPlugins."flotiq.form.sidebar-panel::add"
   */
  const pluginSidebarPanels = usePluginResults(
    'flotiq.form.sidebar-panel::add',
    FormAddSidebarPanelEvent,
    {
      contentType: webhookContentType,
      contentObject: webhook,
      disabled: isLoading,
      duplicate,
      create: !webhookId,
      form: formState,
    },
  );

  const isSaveAllowed = (canUpdate && webhookId) || (canCreate && !webhookId);

  const onCancel = useCallback(() => {
    modalInstance.resolve?.(webhook);
  }, [modalInstance, webhook]);

  const LayoutComponent = inModal ? PoppingPageLayout : PageLayout;

  return (
    <LayoutComponent
      {...(inModal && ({ onBack: onCancel } as any))}
      page="webhooks"
      title={`${mode === 'add' ? t('Global.Add') : t('Global.Edit')} Webhook`}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.Webhooks')}
          parentLink={gridLink}
        />
      }
      buttons={
        <>
          <TopbarCancelButton
            isDirty={formState?.dirty}
            {...(inModal ? { onClick: onCancel } : { link: gridLink })}
          />
          <TopbarSaveButton
            form="webhook-form"
            isLoading={isLoading}
            disabled={disableSave}
            actionDisallowed={!isSaveAllowed}
          />
          <TopbarActionMenu>
            {webhookId && !duplicate && !inModal && (
              <TopbarDuplicateButton
                actionDisallowed={!canCreate}
                onClick={() => onDuplicate(webhook)}
              />
            )}
            <TopbarSaveAndLeaveButton
              form="webhook-form"
              disabled={disableSave}
              actionDisallowed={!isSaveAllowed}
              navigateOnSave={navigateOnSave}
            />
            {mode === 'edit' && !duplicate && (
              <TopbarDeleteButton
                onClick={handleDeleteWebhook}
                actionDisallowed={!canDelete}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withSidebar}>
        <div
          className={predefinedLayoutClasses.leftColumnWhite}
          {...getTestProps(testId, 'container')}
        >
          {isLoading && (
            <div
              className={twMerge(
                'absolute top-0 left-0 w-full h-full flex bg-white dark:bg-slate-800',
                'justify-center items-center z-10 rounded-md opacity-90',
              )}
              {...getTestProps(testId, 'container-loader')}
            >
              <Loader type="spinner-grid" size="small" />
            </div>
          )}

          <WebhookForm
            webhook={webhook as Webhook}
            webhookContentType={webhookContentType}
            onSubmit={handleSave}
            mode={mode}
            codeWebhookAvailable={codeWebhookAvailable}
            contentTypesOptions={contentTypesOptions}
            filterCtdName={filterCtdName}
            disabled={isFormDisabled}
            setFormState={setFormState}
            testId={testId}
          />
        </div>

        <div className={predefinedLayoutClasses.rightColumn}>
          <ContentObjectInformation
            createdAt={duplicate ? null : webhook?.internal?.createdAt}
            updatedAt={duplicate ? null : webhook?.internal?.updatedAt}
            additionalClasses="order-10"
            testId={testId}
          />

          {mode === 'edit' && !duplicate && (
            <WebhookLogs
              logs={webhookLogs}
              loading={webhookLogsLoading}
              testId={testId}
              onRefresh={webhookLogsReload}
              additionalClasses="order-20"
            />
          )}

          {webhookVersions?.length > 0 && !duplicate && webhookId && (
            <ContentObjectVersions
              objectId={webhookId}
              contentType={webhookContentType}
              versions={webhookVersions}
              onRestore={handleVersionRestore}
              currentChanges={formState?.getValues() || {}}
              isDirty={formState?.dirty}
              additionalClasses="order-30"
              testId={testId}
            />
          )}

          {pluginSidebarPanels?.length > 0 && (
            <ElementFromPlugin results={pluginSidebarPanels} />
          )}
        </div>
      </div>
    </LayoutComponent>
  );
};

export default AddWebhooksContainer;
